import React, { useEffect, useRef, useState } from "react";
import Stage from "./diagram/Stage.svg";
import { SpeakerLayoutModal } from "./modals/SpeakerLayout";

import styles from "./HardwareRequirements.module.scss";
import { MixingStageModal } from "./modals/MixingStage";
import { MonitoringLevelsModal } from "./modals/MonitoringLevels";
import { MediaPlayerModal } from "./modals/MediaPlayer";
import { AudioInterfaceModal } from "./modals/AudioInterface";

export type StageElement = "speaker";

interface HardwareRequirementsProps {
  onElementSelect?: (element: StageElement) => void;
}

const HIGHLIGHT_MAPPING: Record<string, string[]> = {
  "speaker-trigger": [
    "Stage_svg__Vector_45",
    "Stage_svg__HR",
    "Stage_svg__FC",
    "Stage_svg__FR",
    "Stage_svg__R",
    "Stage_svg__HRR",
    "Stage_svg__RR",
    "Stage_svg__FL",
    "Stage_svg__Sub",
    "Stage_svg__HRL",
    "Stage_svg__RL",
    "Stage_svg__L",
    "Stage_svg__Vector_303",
    "Stage_svg__Vector_329",
    "Stage_svg__Vector_301",
    "Stage_svg__Vector_41",
    "Stage_svg__Vector_61",
    "Stage_svg__Vector_293",
    "Stage_svg__Vector_292",
    "Stage_svg__S_4",
    "Stage_svg__S_7",
    "Stage_svg__Vector_322",
    "Stage_svg__S_6",
    "Stage_svg__Vector_313",
    "Stage_svg__Vector_312",
    "Stage_svg__Vector_314",
    "Stage_svg__HL",
  ],
  "medial-player-trigger": [
    "Stage_svg__Vector_49",
    "Stage_svg__Vector_50",
    "Stage_svg__Vector_52",
    "Stage_svg__Vector_51",
  ],
  "monitoring-levels-trigger": ["Group_459"],
  "audio-interface-trigger": ["Stage_svg__Vector_72", "Stage_svg__Group_458", "Stage_svg__mix"],
  "mixing-stage-trigger": ["Stage_svg__Vector_241", "Stage_svg__Wall"],
};

export const HardwareRequirements: React.FC<HardwareRequirementsProps> = ({ onElementSelect }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);
  const [isMixingStageModalOpen, setIsMixingStageModalOpen] = useState(false);
  const [isMonitoringLevelsModalOpen, setIsMonitoringLevelsModalOpen] = useState(false);
  const [isMediaPlayerModalOpen, setIsMediaPlayerModalOpen] = useState(false);
  const [isAudioInterfaceModalOpen, setIsAudioInterfaceModalOpen] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, [divRef]);

  useEffect(() => {
    if (!mounted || !divRef.current) return;

    // For each trigger class in the mapping
    Object.entries(HIGHLIGHT_MAPPING).forEach(([triggerClass, targetIds]) => {
      const triggerElements = divRef.current!.querySelectorAll(`.${triggerClass}`);

      triggerElements.forEach((triggerElement) => {
        const handleMouseEnter = (): void => {
          // Highlight the rect inside trigger
          const triggerRect = triggerElement.querySelector("rect");
          if (triggerRect) {
            triggerRect.setAttribute("data-hover", "true");
          }

          // Highlight all elements in mapped IDs
          targetIds.forEach((id) => {
            // Try both with and without the Stage_svg__ prefix
            const possibleIds = [id, `Stage_svg__${id}`];

            possibleIds.forEach((possibleId) => {
              const element = divRef.current!.querySelector(`#${possibleId}`);
              if (element) {
                element.setAttribute("data-hover", "true");
                // Also highlight all children
                element.querySelectorAll("*").forEach((child) => {
                  child.setAttribute("data-hover", "true");
                });
              }
            });
          });
        };

        const handleMouseLeave = (): void => {
          // Remove highlight from the rect inside trigger
          const triggerRect = triggerElement.querySelector("rect");
          if (triggerRect) {
            triggerRect.removeAttribute("data-hover");
          }

          // Remove highlight from all elements in mapped IDs
          targetIds.forEach((id) => {
            // Try both with and without the Stage_svg__ prefix
            const possibleIds = [id, `Stage_svg__${id}`];

            possibleIds.forEach((possibleId) => {
              const element = divRef.current!.querySelector(`#${possibleId}`);
              if (element) {
                element.removeAttribute("data-hover");
                // Also remove highlight from all children
                element.querySelectorAll("*").forEach((child) => {
                  child.removeAttribute("data-hover");
                });
              }
            });
          });
        };

        const handleClick = (): void => {
          if (triggerClass === "speaker-trigger") {
            setIsSpeakerModalOpen(true);
          } else if (triggerClass === "mixing-stage-trigger") {
            setIsMixingStageModalOpen(true);
          } else if (triggerClass === "monitoring-levels-trigger") {
            setIsMonitoringLevelsModalOpen(true);
          } else if (triggerClass === "medial-player-trigger") {
            setIsMediaPlayerModalOpen(true);
          } else if (triggerClass === "audio-interface-trigger") {
            setIsAudioInterfaceModalOpen(true);
          }
          onElementSelect?.("speaker");
        };

        // Add event listeners
        triggerElement.addEventListener("mouseenter", handleMouseEnter);
        triggerElement.addEventListener("mouseleave", handleMouseLeave);
        triggerElement.addEventListener("click", handleClick);

        // Store cleanup function
        return () => {
          triggerElement.removeEventListener("mouseenter", handleMouseEnter);
          triggerElement.removeEventListener("mouseleave", handleMouseLeave);
          triggerElement.removeEventListener("click", handleClick);
        };
      });
    });
  }, [mounted, onElementSelect]);

  return (
    <>
      <div ref={divRef} className={styles.root}>
        <Stage />
      </div>
      <SpeakerLayoutModal visible={isSpeakerModalOpen} onClose={() => setIsSpeakerModalOpen(false)} />
      <MixingStageModal visible={isMixingStageModalOpen} onClose={() => setIsMixingStageModalOpen(false)} />
      <MonitoringLevelsModal
        visible={isMonitoringLevelsModalOpen}
        onClose={() => setIsMonitoringLevelsModalOpen(false)}
      />
      <MediaPlayerModal visible={isMediaPlayerModalOpen} onClose={() => setIsMediaPlayerModalOpen(false)} />
      <AudioInterfaceModal visible={isAudioInterfaceModalOpen} onClose={() => setIsAudioInterfaceModalOpen(false)} />
    </>
  );
};
